import { BetaSchemaForm } from "@ant-design/pro-components";
import { Button } from "antd";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import { useEventEmitter, type EventEmitter } from "../../event-runtime";

type SchemaFormLayoutType = "Form" | "ModalForm" | "DrawerForm";

type RegisterCapabilities = (
  componentId: string,
  capabilities: Record<string, (...args: unknown[]) => unknown>,
) => () => void;

type MaterialRuntime = {
  nodeId: string;
  registerComponentCapabilities?: RegisterCapabilities;
};

interface SchemaFormWrapperProps {
  columns?: any[];
  layoutType?: SchemaFormLayoutType;
  triggerText?: string;
  trigger?: ReactNode;
  title?: string;
  width?: number;
  triggerButtonType?: "link" | "text" | "default" | "primary" | "dashed";
  onFinish?: (
    values: Record<string, unknown>,
  ) => boolean | void | Promise<boolean | void>;
  onEvent?: EventEmitter;
  runtime?: MaterialRuntime;
  __nodeId?: string;
  __registerCapabilities?: RegisterCapabilities;
  [key: string]: unknown;
}

function SchemaFormWrapper({
  columns = [],
  layoutType = "Form",
  triggerText,
  layout = "horizontal",
  trigger,
  title,
  width,
  triggerButtonType,
  onFinish,
  onEvent,
  runtime,
  __nodeId,
  __registerCapabilities,
  ...rest
}: SchemaFormWrapperProps) {
  const formRef = useRef<any>(null);
  const componentNodeId = runtime?.nodeId || __nodeId;
  const registerCapabilities =
    runtime?.registerComponentCapabilities || __registerCapabilities;
  const popupMode = layoutType === "ModalForm" || layoutType === "DrawerForm";
  const emitEvent = useEventEmitter(onEvent);
  const handleClick = () => {
    emitEvent<boolean | void>("schemaForm.triggerClick", {});
  };
  const finalTrigger =
    trigger ??
    (popupMode && triggerText ? (
      <Button type={triggerButtonType ?? "primary"} onClick={handleClick}>
        {triggerText}
      </Button>
    ) : undefined);

  useEffect(() => {
    if (!formRef.current) return;
    emitEvent<boolean | void>("schemaForm.init", {});
  }, [emitEvent]);

  useEffect(() => {
    if (!componentNodeId || !registerCapabilities) return;
    return registerCapabilities(componentNodeId, {
      setFieldsValue: ((values: Record<string, unknown>) =>
        formRef.current?.setFieldsValue?.(values)) as unknown as (...args: unknown[]) => unknown,
      setFieldValue: ((name: string, value: unknown) =>
        formRef.current?.setFieldValue?.(name, value)) as unknown as (...args: unknown[]) => unknown,
      getFieldsValue: (() =>
        formRef.current?.getFieldsValue?.()) as unknown as (...args: unknown[]) => unknown,
      getFieldValue: ((name: string) =>
        formRef.current?.getFieldValue?.(name)) as unknown as (...args: unknown[]) => unknown,
      resetFields: (() =>
        formRef.current?.resetFields?.()) as unknown as (...args: unknown[]) => unknown,
      validateFields: (() =>
        formRef.current?.validateFields?.()) as unknown as (...args: unknown[]) => unknown,
      submit: (() =>
        formRef.current?.submit?.()) as unknown as (...args: unknown[]) => unknown,
    });
  }, [componentNodeId, registerCapabilities]);

  const finalOnFinish = useCallback(
    async (values: Record<string, unknown>) => {
      try {
        let finalResult: boolean | void = undefined;

        const eventResult = await emitEvent<any | void>(
          "schemaForm.finish",
          values,
        );
        if (eventResult !== undefined) {
          finalResult = eventResult;
          if (eventResult?.code === 200) {
            emitEvent<boolean | void>("schemaForm.finishSuccess", eventResult);
          }
        }

        return typeof finalResult === "boolean" ? finalResult : true;
      } catch (error) {
        return false;
      }
    },
    [emitEvent, onFinish],
  );

  const handlePopupOpenChange = (open: boolean) => {
    if (!open) {
      formRef.current?.resetFields?.();
    }
    emitEvent<boolean | void>("onOpenChange", open);
  };

  const popupModeProps = popupMode
    ? {
        onOpenChange: handlePopupOpenChange,
        modalProps: {
          forceRender: true,
        },
        drawerProps: {
          forceRender: true,
        },
      }
    : {};

  const schemaRenderKey = useMemo(() => {
    return JSON.stringify({
      layoutType,
      triggerText,
      columns,
    });
  }, [layoutType, triggerText, columns]);

  return (
    <BetaSchemaForm
      key={schemaRenderKey}
      formRef={formRef}
      destroyOnClose
      layoutType={layoutType as any}
      layout={layout as any}
      columns={columns as any}
      trigger={finalTrigger}
      title={title}
      width={width}
      {...(rest as any)}
      {...popupModeProps}
      onFinish={finalOnFinish}
    />
  );
}

export default SchemaFormWrapper;
